/**
 * Utility functions to map API responses to app types
 */

import type {
  CardResponse,
  ProfileSummary,
  MatchResponse,
  Match,
  MessageResponse,
  Message,
  BadgeLevel,
} from '@/types';

/**
 * Convert API CardResponse to ProfileSummary
 */
export function mapCardToProfileSummary(card: CardResponse): ProfileSummary {
  // Convert badge level to app format (capitalize first letter)
  const level = card.verification_badge_level
    ? (card.verification_badge_level.charAt(0).toUpperCase() + card.verification_badge_level.slice(1)) as 'Junior' | 'Middle' | 'Senior' | 'Expert'
    : undefined;

  // Safely extract photos
  const photos = Array.isArray(card.photos) && card.photos.length > 0
    ? card.photos.map(p => p.photo_url)
    : [];

  return {
    id: card.id.toString(),
    name: card.first_name,
    age: card.age,
    role: card.specializations?.[0]?.name,
    company: undefined, // Not provided in CardResponse
    level,
    itTestScore: card.verification_score || undefined,
    verified: card.is_verified,
    techStack: Array.isArray(card.tech_stack) ? card.tech_stack.map(t => t.name) : [],
    photos,
    aiMatch: undefined,
    desiredLifestyle: card.lifestyle_tag ? `${card.lifestyle_tag.icon} ${card.lifestyle_tag.name}` : undefined,
    distanceKm: card.distance_km || undefined,
    commonHobbies: card.common_hobbies?.map(h => h.name),
    city: card.city || undefined,
    country: undefined, // Not provided in CardResponse yet
  };
}

/**
 * Convert API MatchResponse to Match
 */
export function mapMatchResponseToMatch(matchResponse: MatchResponse, currentUserId: string): Match {
  const matchedUser: ProfileSummary = {
    id: matchResponse.matched_user.id.toString(),
    name: matchResponse.matched_user.first_name,
    age: matchResponse.matched_user.age,
    verified: matchResponse.matched_user.is_verified,
    itTestScore: matchResponse.matched_user.verification_score || undefined,
    level: matchResponse.matched_user.verification_badge_level
      ? (matchResponse.matched_user.verification_badge_level.charAt(0).toUpperCase() + 
         matchResponse.matched_user.verification_badge_level.slice(1)) as 'Junior' | 'Middle' | 'Senior' | 'Expert'
      : undefined,
    techStack: [],
    photos: matchResponse.matched_user.main_photo ? [matchResponse.matched_user.main_photo] : [],
  };

  // Create minimal current user profile - will be filtered out by getOtherUser
  const currentUser: ProfileSummary = {
    id: currentUserId,
    name: 'Current User',
    age: 0,
    verified: false,
    techStack: [],
    photos: [],
  };

  const lastMessage: Message | undefined = matchResponse.last_message
    ? {
        id: matchResponse.last_message.id.toString(),
        matchId: matchResponse.id.toString(),
        senderId: matchResponse.last_message.sender_id.toString(),
        text: matchResponse.last_message.content,
        timestamp: matchResponse.last_message.created_at,
        read: matchResponse.last_message.is_read,
      }
    : undefined;

  return {
    id: matchResponse.id.toString(),
    // Put matched user first, then current user (so find will get matched user if lookup fails)
    users: [matchedUser, currentUser],
    createdAt: matchResponse.created_at,
    lastMessage,
    unreadCount: matchResponse.unread_count,
    isStarred: false,
  };
}

/**
 * Convert API MessageResponse to Message
 */
export function mapMessageResponseToMessage(
  msgResponse: MessageResponse,
  matchId: string
): Message {
  return {
    id: msgResponse.id.toString(),
    matchId,
    senderId: msgResponse.sender_id.toString(),
    text: msgResponse.content,
    timestamp: msgResponse.created_at,
    read: msgResponse.is_read,
    type: 'text',
    // Новые поля Messaging 2.0
    photoUrl: msgResponse.photo_url,
    replyToMessageId: msgResponse.reply_to_message_id?.toString(),
    repliedMessage: msgResponse.replied_message 
      ? mapMessageResponseToMessage(msgResponse.replied_message, matchId)
      : null,
    deletedAt: msgResponse.deleted_at,
    editedAt: msgResponse.edited_at,
    isEdited: msgResponse.is_edited,
  };
}

/**
 * Get other user from match
 */
export function getOtherUser(match: Match, currentUserId: string): ProfileSummary {
  return match.users.find(u => u.id !== currentUserId) || match.users[0];
}

